<template>
  <main class="profile">
    <b-container v-if="user">
      <form @submit.prevent="saveForm(true)">
        <b-row>
          <b-col offset="1">
            <h1>{{ user.username }}</h1>
            <h5>{{ $t(user.state) }}</h5>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col
            lg="5"
            offset="1"
          >
            <dx-fieldset-header :text="$t('User info')" />
            <dx-form-group
              v-for="it in userInfoFields"
              :key="it.key"
              :label="it.label"
            >
              <dx-text-box
                v-model="user[it.key]"
                value-change-event="input"
                styling-mode="outlined"
              >
                <dx-validator>
                  <dx-required-rule
                    v-if="it.isRequired"
                    :message="$t('The field is required')"
                  />
                  <dx-email-rule
                    v-if="it.isEmail"
                    :message="$t('Email is invalid')"
                  />
                </dx-validator>
              </dx-text-box>
            </dx-form-group>
          </b-col>
          <b-col lg="6">
            <dx-fieldset-header :text="$t('Change password')" />
            <dx-form-group :label="$t('new password')">
              <dx-text-box
                v-model="user.password"
                :mode.sync="passwordMode"
                value-change-event="input"
                styling-mode="outlined"
              >
                <dx-text-box-button
                  location="after"
                  name="generatePassword"
                  :options="generatePasswordButton"
                >
                </dx-text-box-button>
                <dx-text-box-button
                  name="password"
                  :options="passwordButton"
                  location="after"
                />
              </dx-text-box>
            </dx-form-group>
            <dx-form-group :label="$t('password confirmation')">
              <dx-text-box
                v-model="passwordConfirm"
                :mode.sync="passwordMode"
                value-change-event="input"
                styling-mode="outlined"
                :is-valid="!errors.passwordConfirm"
              >
                <dx-text-box-button
                  :options="passwordButton"
                  name="password"
                  location="after"
                />
              </dx-text-box>
              <div
                v-if="errors.passwordConfirm"
                class="my-1 text-danger"
              >
                {{ errors.passwordConfirm }}
              </div>
            </dx-form-group>
          </b-col>
          <b-col
            lg="8"
            offset="1"
            class="d-flex flex-column flex-md-row align-items-center mt-3"
          >
            <dx-button
              :text="$t('Save')"
              type="success"
              styling-mode="outlined"
              class="custom-btn mb-3 mb-lg-0 mr-2"
              :disabled="loading"
              @click="saveForm"
            />
            <dx-button
              :text="$t('Save and send to confirmation')"
              :use-submit-behavior="true"
              type="success"
              styling-mode="contained"
              class="custom-btn mb-3 mb-lg-0 mr-2"
              :disabled="loading"
            />
            <dx-button
              :text="$t('Reset')"
              type="success"
              styling-mode="text"
              class="custom-btn"
              :disabled="loading"
              @click="resetForm"
            />
          </b-col>
        </b-row>
      </form>
    </b-container>
    <dx-load-indicator
      v-else
      class="mx-auto"
      :height="60"
      :width="60"
    />
  </main>
</template>

<script>
import clone from 'lodash.clonedeep';

import { DxTextBox, DxButton } from 'devextreme-vue';
import { DxLoadIndicator } from 'devextreme-vue/load-indicator';

import { DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';

import { DxValidator, DxRequiredRule, DxEmailRule } from 'devextreme-vue/validator';

import DxFormGroup from '@/components/common/DxFormGroup';
import DxFieldsetHeader from '@/components/common/DxFieldsetHeader';

const generator = require('generate-password');

export default {
  name: 'Profile',
  components: {
    DxTextBox,
    DxButton,
    DxLoadIndicator,
    DxFormGroup,
    DxFieldsetHeader,
    DxValidator,
    DxRequiredRule,
    DxEmailRule,
    DxTextBoxButton,
  },
  data: vm => ({
    user: null,
    loading: false,
    passwordConfirm: '',
    errors: {
      password: null,
      passwordConfirm: null,
    },
    passwordMode: 'text',
    passwordButton: {
      icon: 'ace-icon icon-info',
      type: 'text',
      onClick: () => {
        vm.passwordMode = vm.passwordMode === 'text' ? 'password' : 'text';
      },
    },
    generatePasswordButton: {
      text: vm.$t('generate password'),
      stylingMode: 'outline',
      type: 'default',
      elementAttr: {
        class: 'dx-button-success',
      },
      onClick: () => {
        vm.generatePassword();
      },
    },
  }),
  computed: {
    userInfoFields() {
      return [
        {
          key: 'first_name',
          label: this.$t('First name'),
          isRequired: true,
        },
        {
          key: 'last_name',
          label: this.$t('Last name'),
          isRequired: true,
        },
        {
          key: 'phone',
          label: this.$t('Phone'),
          isRequired: true,
        },
        {
          key: 'email',
          label: this.$t('Email'),
          isRequired: true,
          isEmail: true,
        },
      ];
    },
  },
  created() {
    this.getUser();
  },
  methods: {
    async getUser() {
      this.user = await this.$http.get('profile/api/users/me/');
    },
    async saveForm(check) {
      this.loading = true;
      try {
        let user;

        if (!this.isFormValid()) {
          return;
        }

        if (check) {
          user = await this.$http.put('profile/api/users/me/', {
            ...this.user,
            status: 'pending',
          });
        } else {
          user = await this.$http.put('profile/api/users/me/', this.user);
        }

        this.$store.commit('setUser', user);
      } finally {
        this.loading = false;
      }
    },
    resetForm() {
      this.user = clone(this.$store.state.user);
    },
    isFormValid() {
      this.errors.passwordConfirm = '';
      let hasErrors = false;

      if (this.user.password && this.user.password !== this.passwordConfirm) {
        this.errors.passwordConfirm = this.$t('Password and confirmation do not match');
        hasErrors = true;
      }

      return !hasErrors;
    },
    generatePassword() {
      this.user.password = generator.generate({
        length: 8,
      });

      this.passwordConfirm = this.user.password;
    },
  },
};
</script>

<style lang="scss" scoped>
.profile {
  display: flex;
  padding-top: 30px;
  padding-bottom: 30px;
  color: #0e385e;
}

h1 {
  font-size: 24px;

  @media (min-width: 500px) {
    font-size: 40px;
  }
}
</style>
